// React
import { useEffect, useLayoutEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
// ライブラリ
import dayjs from "dayjs";
import Swal from "sweetalert2";
// コンポーネント
import SkillTagFilter from "../../../components/input/SkillTagFilter/SkillTagFilter";
// カスタムフック
import { useSkillTags } from "../../../hooks/useSkillTags";
// ユーティリティ
import { menu } from "../../../constants";
import { validationSchema } from "../../../utils/validationUtils";
import { errorMessages } from "../../../utils/messagesUtils";
import { formatGender, calculateAge } from "../../../utils/webCvUtils";

import sty from "./EngineerList.module.css";

const EngineerList = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [appliedTags, setAppliedTags] = useState([]); // 実際の検索に適用されるタグ
  const [tagCategories, setTagCategories] = useState({});
  const [showMemoModal, setShowMemoModal] = useState(false);
  const [isEditingMemo, setIsEditingMemo] = useState(false);
  const [memoType, setMemoType] = useState("common"); // "common" | "sales"
  const [currentMemo, setCurrentMemo] = useState({
    name: "",
    email: "",
    memo: "", // メモタイプ
  });
  const [editingMemoText, setEditingMemoText] = useState("");
  const [publicStates, setPublicStates] = useState({});

  const { transformSkillTagsData, filterEngineers } = useSkillTags({
    selectedTags,
    setSelectedTags,
    tagCategories,
    mode: "filter",
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    clearErrors,
  } = useForm({
    mode: "onSubmit", // 保存時にバリデーションを実行
    reValidateMode: "onSubmit", // 一度エラーが発生した後も、次の保存時にのみ再バリデーションを実行
    defaultValues: {
      memo: "",
    },
  });

  useLayoutEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/mng/engineers`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
            credentials: "include",
          },
        );
        const result = await response.json();
        if (response.status === 200 && result.status === "OK") {
          setData(result.data);

          // 各エンジニアのis_public状態を初期化
          const initialPublicStates = {};
          result.data.forEach((engineer) => {
            const isPublicRaw =
              engineer?.skillsheetBox?.[0]?.skillsheet?.input?.is_public;
            const isPublic =
              isPublicRaw === "true"
                ? true
                : isPublicRaw === "false"
                  ? false
                  : false;
            initialPublicStates[engineer.email] = isPublic;
          });
          setPublicStates(initialPublicStates);
        } else {
          if (response.status === 401) {
            navigate(menu.MNG.SIGNIN.PATH);
          } else {
            const error = new Error();
            error.front_message = errorMessages.common.data_fetch_failed;
            throw error;
          }
        }
      } catch (e) {
        setData([]);
        await Swal.fire({
          icon: "error",
          html: `${e.front_message || errorMessages.api.server_error}`,
          customClass: {
            htmlContainer: "large-text",
          },
        });
      } finally {
        setIsLoading(false);
      }
    };

    const fetchSkillTagsData = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/skill-tags?userType=mng`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
            credentials: "include",
          },
        );
        const result = await response.json();
        if (response.status === 200 && result.status === "OK") {
          setTagCategories(transformSkillTagsData(result.data));
        } else {
          if (response.status === 401) {
            navigate(menu.MNG.SIGNIN.PATH);
          } else {
            const error = new Error();
            error.front_message = errorMessages.common.data_fetch_failed;
            throw error;
          }
        }
      } catch (e) {
        await Swal.fire({
          icon: "error",
          html: `${e.front_message || errorMessages.api.server_error}`,
          customClass: {
            htmlContainer: "large-text",
          },
        });
      }
    };

    fetchData();
    fetchSkillTagsData();
  }, [navigate, currentMemo.memo]);

  // 検索実行ハンドラー
  const handleSearch = (tagsToApply = null) => {
    const tagsToUse = tagsToApply !== null ? tagsToApply : selectedTags;
    setAppliedTags([...tagsToUse]);
  };

  // 全ての日付を比較して最新の更新日を返す
  const getLatestUpdateDate = (engineer) => {
    const dates = [
      engineer?.profile?.updatedAt,
      engineer?.certifications?.updatedAt,
      engineer?.projects?.updatedAt,
      engineer?.works?.updatedAt,
    ].filter((date) => date); // null や undefined を除外

    if (dates.length === 0) return null;

    return dates.reduce((latest, current) => {
      return dayjs(current).isAfter(dayjs(latest)) ? current : latest;
    });
  };

  // 名前表示の関数
  const getDisplayName = (engineer) => {
    const lastName = engineer?.profile?.last_name;
    const firstName = engineer?.profile?.first_name;
    // フルネームが未登録の場合はメールアドレスを表示
    if (!lastName || !firstName) {
      return engineer?.email;
    }
    return `${lastName} ${firstName}`;
  };

  // データフィルタリング
  const filteredData = filterEngineers(data, appliedTags, "profile.skillTags");

  const handleShowMemo = (engineer, type = "common") => {
    resetValidationErrors();
    setMemoType(type);
    // メモタイプを設定
    const memoData =
      type === "common" ? engineer?.mng_memo : engineer?.sales_memo; // メモデータを取得
    const memoContent = memoData?.content || ""; // メモ内容を取得

    // 現在のメモ情報をセット
    setCurrentMemo({
      // 現在のメモ情報をセット
      name:
        (engineer?.profile?.last_name || "") +
          (engineer?.profile?.first_name || "") || engineer?.email,
      email: engineer?.email,
      memo: memoContent, // メモ内容をセット
    });
    // 編集モードをリセット
    setIsEditingMemo(false);
    setEditingMemoText(memoContent);
    setValue("memo", memoContent);
    // モーダルを表示
    setShowMemoModal(true);
  };

  const handleCloseMemo = () => {
    setShowMemoModal(false);
    setIsEditingMemo(false);
    setEditingMemoText("");
    resetValidationErrors();
  };

  const handleEditMemo = async (data) => {
    const typePath = memoType === "sales" ? "sales-memo" : "memo"; // 共通メモ or 営業メモ
    // 編集中ユーザーがいるか判定
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/mng/engineers/${typePath}/lock/check`, // 共通メモ or 営業メモ
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            email: currentMemo.email,
          }),
        },
      );
      const result = await response.json();
      if (response.status === 200 && result.status === "OK") {
        handleCreateMemoLock();
      } else if (response.status === 401) {
        navigate(menu.MNG.SIGNIN.PATH);
      } else if (response.status === 409) {
        const error = new Error();
        error.icon = "info";
        error.front_message = result.message;
        throw error;
      } else {
        const error = new Error();
        error.front_message = errorMessages.common.data_not_found;
        throw error;
      }
    } catch (e) {
      await Swal.fire({
        icon: e.icon || "error",
        html: `${e.front_message || errorMessages.api.server_error}`,
        customClass: {
          htmlContainer: "large-text",
        },
      });
    }
  };
  // メモ編集ロック作成ハンドラー
  const handleCreateMemoLock = async (typePath) => {
    try {
      const path = typePath || (memoType === "sales" ? "sales-memo" : "memo");
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/mng/engineers/${path}/lock/create`, // 共通メモ or 営業メモ
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            email: currentMemo.email,
          }),
        },
      );
      const result = await response.json();

      if (response.status === 200 && result.status === "OK") {
        setValue("memo", currentMemo.memo);
        setIsEditingMemo(true);
      } else if (response.status === 404) {
        const error = new Error();
        error.front_message = errorMessages.common.data_not_found;
        throw error;
      } else {
        throw Error();
      }
    } catch (e) {
      await Swal.fire({
        icon: "error",
        html: `${e.front_message || errorMessages.api.server_error}`,
        customClass: {
          htmlContainer: "large-text",
        },
      });
    }
  };

  // メモ保存ハンドラー
  const handleSaveMemo = async (data) => {
    const typePath = memoType === "sales" ? "sales-memo" : "memo";
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/mng/engineers/${typePath}/create`, // 共通メモ or 営業メモ
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            email: currentMemo.email,
            memo: data.memo,
          }),
        },
      );
      const result = await response.json();
      if (response.status === 200 && result.status === "OK") {
        setCurrentMemo({
          ...currentMemo,
          memo: data.memo,
        });
        setIsEditingMemo(false);
        setEditingMemoText("");
      } else {
        if (response.status === 401) {
          navigate(menu.MNG.SIGNIN.PATH);
        } else if (response.status === 404) {
          const error = new Error();
          error.front_message = errorMessages.common.data_not_found;
          throw error;
        } else {
          const error = new Error();
          error.front_message = errorMessages.common.registration_failed;
          throw error;
        }
      }
    } catch (e) {
      await Swal.fire({
        icon: "error",
        html: `${e.front_message || errorMessages.api.server_error}`,
        customClass: {
          htmlContainer: "large-text",
        },
      });
    }
  };

  const handleCancelEdit = () => {
    setIsEditingMemo(false);
    resetValidationErrors();
  };

  // バリデーションメッセージをリセットする関数
  const resetValidationErrors = () => {
    clearErrors("memo");
  };

  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    setCharCount(editingMemoText.length);
  }, [editingMemoText]);

  const handleTextAreaChange = (e) => {
    const value = e.target.value;
    setCharCount(value.length);
  };

  const handleChangeSkillsheetPublic = async (engineerEmail, newValue) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/mng/engineers/skillsheet/public`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            engineer_email: engineerEmail,
            is_public: newValue,
          }),
        },
      );
      const result = await response.json();
      if (response.status === 200 && result.status === "OK") {
        setPublicStates((prev) => ({
          ...prev,
          [engineerEmail]: newValue,
        }));
      } else {
        if (response.status === 401) {
          navigate(menu.MNG.SIGNIN.PATH);
        } else {
          const error = new Error();
          error.front_message = errorMessages.common.update_failed;
          throw error;
        }
      }
    } catch (e) {
      await Swal.fire({
        icon: "error",
        html: `${e.front_message || errorMessages.api.server_error}`,
        customClass: {
          htmlContainer: "large-text",
        },
      });
    }
  };

  return (
    <>
      <div
        className={`bg ${sty["bg--engineerlist-sp"]} ${showMemoModal ? sty["memo-open"] : ""}`}
      >
        <div className="page-tit">
          <div className="page-tit-outer">
            <p className="page-tit-txt">BLOWISH</p>
            <p className="page-tit-txt">エンジニア一覧</p>
            <p className="page-tit-txt page-tit-txt--mng">［管理者用］</p>
            <div className="page-tit-border"></div>
          </div>
        </div>

        <SkillTagFilter
          selectedTags={selectedTags}
          setSelectedTags={setSelectedTags}
          tagCategories={tagCategories}
          onSearch={handleSearch}
        />

        <div className={sty["list-pc"]}>
          <div className={sty["list-pc__table"]}>
            <div className={sty["list-pc__tr-h"]}>
              <div className={sty["list-pc__th"]}>氏名</div>
              <div className={sty["list-pc__th"]}>職種分類</div>
              <div className={sty["list-pc__th"]}>性別</div>
              <div className={sty["list-pc__th"]}>年齢</div>
              <div className={sty["list-pc__th"]}>スキルタグ</div>
              <div className={sty["list-pc__th"]}>更新日時</div>
              <div className={sty["list-pc__th"]}>共有メモ</div>
              <div className={sty["list-pc__th"]}>スキルシート公開</div>
              {/* 営業メモ */}
              <div className={sty["list-pc__th"]}>営業メモ</div>
            </div>

            {isLoading ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px 0",
                  fontSize: "1.2em",
                }}
              >
                Loading...
              </div>
            ) : filteredData.length > 0 ? (
              filteredData.map((engineer, index) => {
                {
                  /* ミニマムリリースでは、スキルシートは1つしか登録しないため、スキルシート1つ目のランダムIDを取得する */
                }
                const hasSkillsheet =
                  engineer?.skillsheetBox?.[0]?.skillsheet?.output?.random1Id &&
                  engineer?.skillsheetBox?.[0]?.skillsheet?.output?.random2Id;
                const linkPath =
                  hasSkillsheet &&
                  `/preview/${engineer.skillsheetBox[0].skillsheet.output.random1Id}/${engineer.skillsheetBox[0].skillsheet.output.random2Id}`;
                // publicStatesから状態を取得（存在しない場合はfalse）
                const isPublic = publicStates[engineer.email] ?? false;

                return (
                  <div
                    className={sty["list-pc__tr-d"]}
                    key={engineer?.email || `engineer-pc-${index}`}
                  >
                    <div className={sty["list-pc__td"]}>
                      {getDisplayName(engineer)}
                    </div>
                    <div className={sty["list-pc__td"]}>
                      <div>{engineer?.profile?.job_title || "未登録"}</div>
                      {engineer?.profile?.job_title2 && (
                        <div>{engineer?.profile?.job_title2}</div>
                      )}
                    </div>
                    <div className={sty["list-pc__td"]}>
                      {formatGender(engineer?.profile?.gender)}
                    </div>
                    <div className={sty["list-pc__td"]}>
                      {calculateAge(
                        engineer?.profile?.birth_year,
                        engineer?.profile?.birth_month,
                        engineer?.profile?.birth_day,
                      )}
                    </div>
                    <div
                      className={`${sty["list-pc__td"]} ${sty["skill-tag-container"]}`}
                    >
                      {(engineer?.profile?.skillTags || []).length > 0
                        ? (engineer?.profile?.skillTags || []).map(
                            (skill, index) => (
                              <div key={index} className={sty["skill-tag"]}>
                                {skill}
                              </div>
                            ),
                          )
                        : "未登録"}
                    </div>
                    <div className={sty["list-pc__td"]}>
                      {getLatestUpdateDate(engineer)
                        ? dayjs(getLatestUpdateDate(engineer)).format(
                            "YYYY/MM/DD HH:mm",
                          )
                        : "未登録"}
                    </div>
                    <div className={sty["list-pc__td"]}>
                      <img
                        src="/images/description.svg"
                        alt="共有メモ"
                        onClick={() => {
                          handleShowMemo(engineer, "common");
                        }}
                        className={sty["list-icon"]}
                      />
                    </div>
                    <div className={sty["list-pc__td"]}>
                      <div>
                        {hasSkillsheet && (
                          <input
                            type="checkbox"
                            checked={isPublic}
                            onChange={(e) => {
                              handleChangeSkillsheetPublic(
                                engineer.email,
                                e.target.checked,
                              );
                            }}
                            className={sty["list-toggle"]}
                          />
                        )}
                      </div>
                    </div>
                    {/* スキルシートが存在する場合のみリンクを表示 */}
                    {hasSkillsheet && (
                      <Link
                        to={linkPath}
                        target="_blank"
                        rel="noreferrer noopener"
                      ></Link>
                    )}
                    {/* 営業メモアイコン */}
                    <div className={sty["list-pc__td"]}>
                      <img
                        src="/images/description.svg"
                        alt="営業メモ"
                        onClick={() => {
                          handleShowMemo(engineer, "sales");
                        }}
                        className={sty["list-icon"]}
                      />
                    </div>
                  </div>
                );
              })
            ) : (
              <div className={sty["list__no-engineers"]}>
                {appliedTags.length > 0
                  ? "検索条件に一致するエンジニアが見つかりません"
                  : "エンジニアが登録されていません"}
              </div>
            )}
          </div>
        </div>

        <div className={sty["list-sp"]}>
          <div className={sty["list-sp__cards"]}>
            {isLoading ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px 0",
                  fontSize: "1.2em",
                }}
              >
                Loading...
              </div>
            ) : filteredData.length > 0 ? (
              filteredData.map((engineer, index) => {
                {
                  /* ミニマムリリースでは、スキルシートは1つしか登録しないため、スキルシート1つ目のランダムIDを取得する */
                }
                const hasSkillsheet =
                  engineer?.skillsheetBox?.[0]?.skillsheet?.output?.random1Id &&
                  engineer?.skillsheetBox?.[0]?.skillsheet?.output?.random2Id;
                const linkPath =
                  hasSkillsheet &&
                  `/preview/${engineer.skillsheetBox[0].skillsheet.output.random1Id}/${engineer.skillsheetBox[0].skillsheet.output.random2Id}`;
                // publicStatesから状態を取得（存在しない場合はfalse）
                const isPublic = publicStates[engineer.email] ?? false;

                return (
                  <div
                    key={engineer?.email || `engineer-sp-${index}`}
                    className={sty["list-sp__card"]}
                  >
                    <div className={sty["list-sp__card-header"]}>
                      <div className={sty["list-sp__profile"]}>
                        <div className={`${sty["list-sp__name"]}`}>
                          {getDisplayName(engineer)}
                        </div>
                        {engineer?.profile && (
                          <>
                            {formatGender(engineer?.profile?.gender)}/
                            {calculateAge(
                              engineer?.profile?.birth_year,
                              engineer?.profile?.birth_month,
                              engineer?.profile?.birth_day,
                            )}
                          </>
                        )}
                      </div>
                      <img
                        src="/images/description.svg"
                        alt="共有メモ"
                        onClick={() => {
                          handleShowMemo(engineer, "common");
                        }}
                        className={sty["list-icon"]}
                      />
                      {/* // 営業メモアイコン */}
                      <img
                        src="/images/description.svg"
                        alt="営業メモ"
                        onClick={() => {
                          handleShowMemo(engineer, "sales");
                        }}
                        className={sty["list-icon"]}
                      />
                    </div>
                    <div className={sty["list-sp__card-content"]}>
                      <div className={`${sty["list-sp__job-title"]}`}>
                        <div>{engineer?.profile?.job_title || "未登録"}</div>
                        {engineer?.profile?.job_title2 && (
                          <div>{engineer?.profile?.job_title2}</div>
                        )}
                      </div>
                      <div className={`${sty["list-sp__skill-tags"]}`}>
                        {(engineer?.profile?.skillTags || []).length > 0
                          ? (engineer?.profile?.skillTags || []).map(
                              (skill, index) => (
                                <div key={index} className={sty["skill-tag"]}>
                                  {skill}
                                </div>
                              ),
                            )
                          : "未登録"}
                      </div>
                      <div className={`${sty["list-sp__updated-date"]}`}>
                        <img
                          src="/images/update.svg"
                          alt="更新日時"
                          className={sty["list-icon"]}
                        />
                        {getLatestUpdateDate(engineer)
                          ? dayjs(getLatestUpdateDate(engineer)).format(
                              "YYYY/MM/DD HH:mm",
                            )
                          : "未登録"}
                      </div>
                      {hasSkillsheet && (
                        <div className={sty["list-sp__public-toggle"]}>
                          <div className={sty["list-sp__public-label"]}>
                            スキルシート公開
                          </div>
                          <input
                            type="checkbox"
                            checked={isPublic}
                            onChange={(e) => {
                              handleChangeSkillsheetPublic(
                                engineer.email,
                                e.target.checked,
                              );
                            }}
                            className={sty["list-toggle"]}
                          />
                        </div>
                      )}
                    </div>
                    {/* スキルシートが存在する場合のみリンクを表示 */}
                    {hasSkillsheet && (
                      <Link
                        to={linkPath}
                        target="_blank"
                        rel="noreferrer noopener"
                      ></Link>
                    )}
                  </div>
                );
              })
            ) : (
              <div className={sty["list__no-engineers"]}>
                {appliedTags.length > 0
                  ? "検索条件に一致するエンジニアが見つかりません"
                  : "エンジニアが登録されていません"}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 共有・営業メモ*/}
      {showMemoModal && (
        <form
          onSubmit={handleSubmit(handleSaveMemo)}
          className={sty["memo-modal"]}
        >
          <div className={sty["memo-modal__header"]}>
            <img
              src="/images/close.svg"
              alt="閉じる"
              onClick={handleCloseMemo}
              className={sty["list-icon"]}
            />
            <div className={sty["memo-modal__title"]}>
              {/* メモタイプ表示 */}
              {memoType === "sales" ? "[営業] " : "[共有] "}　{currentMemo.name}
            </div>
            <img
              src="/images/edit.svg"
              alt="編集"
              onClick={handleEditMemo}
              className={sty["list-icon"]}
            />
          </div>
          <div className={sty["memo-modal__content"]}>
            {!isEditingMemo ? (
              <div>{currentMemo.memo}</div>
            ) : (
              <>
                <div className={sty["memo-modal__textarea-wrapper"]}>
                  <textarea
                    value={editingMemoText}
                    {...register("memo", validationSchema.memo())}
                    onChange={(e) => {
                      setEditingMemoText(e.target.value);
                      handleTextAreaChange(e);
                    }}
                    className={sty["memo-modal__textarea"]}
                    placeholder="メモを入力してください"
                  />
                  <div
                    className={`${sty["memo-modal__char-count"]} ${charCount > 900 ? sty["warning"] : ""} ${charCount > 1000 ? sty["exceeded"] : ""}`}
                  >
                    {`${charCount || 0} / 1000`}
                  </div>
                </div>
              </>
            )}
          </div>
          {isEditingMemo && (
            <div className={sty["memo-modal__footer"]}>
              {errors.memo && (
                <p className="form__validation-msg">{errors.memo.message}</p>
              )}
              <button type="submit" className={sty["memo-modal__save-button"]}>
                保存
              </button>
              <button
                type="button"
                onClick={handleCancelEdit}
                className={sty["memo-modal__cancel-button"]}
              >
                キャンセル
              </button>
            </div>
          )}
        </form>
      )}
    </>
  );
};

export default EngineerList;
